import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableHead,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CollectionsIcon from "@mui/icons-material/Collections";
import GalleryUpload from "./Utils/GalleryUpload";
import { useSelector, useDispatch } from "react-redux";
import Attachments from "./Utils/Attachments";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { buildMedia } from "../../Utils/AuthService";
import { StyledContainer } from "../../CSS/Styles";
import Pagination from "@mui/material/Pagination";
import {
  fetchPromotions,
  savePromotion,
  deleteSectionData,
  orderSectionData,
  getPromotion,
} from "../../../redux/slices/orgAdmin/promotionsSlice";
import { fetchLanguages } from "../../../redux/slices/superAdmin/languagesSlice";
import { StyledButton, StyledTableCell } from "../../CSS/Styles";
import { rowsPerPage } from "../../../Constants";

export default function Promotions() {
  const dispatch = useDispatch();
  const { promotionsContent } = useSelector((state) => state.promotions);
  const { languagesList } = useSelector((state) => state.languages);
  const { companyId } = useSelector((state) => state.app);

  const [isAddingPromotion, setIsAddingPromotion] = useState(false);
  const defaultLanguage =
    languagesList.find((lang) => lang.is_default === 1)?.code || "en";
  const [currentLanguage, setCurrentLanguage] = useState(defaultLanguage);
  const [promotionData, setPromotionData] = useState({});
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promotionIdToDelete, setPromotionIdToDelete] = useState(null);
  const [localPromotions, setLocalPromotions] = useState([]);
  const [deleteTitle, setDeleteTitle] = useState("");
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [galleryId, setGalleryId] = useState(null);

  const [rowToEdit, setRowToEdit] = useState(null);

  const availableLanguages = languagesList.map((lang) => lang.code);

  const files = {};

  const [page, setPage] = useState(1);
  const sectionId = 1;
  const totalRecords =
    promotionsContent?.pagination?.totalRecords || localPromotions.length;
  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  const [media, setMedia] = useState(null);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleDeleteMainImage = (isDeleted) => {
    setPromotionData((prev) => ({
      ...prev,
      deleteMainImage: isDeleted, // ✅ Dynamically update promotionData
    }));
  };

  const handleDeleteAttachment = (language, attachmentId) => {
    setPromotionData((prev) => {
      const updatedDeletedAttachments = new Set(prev.deletedAttachments || []);

      // Add language to the Set if an attachment was deleted
      updatedDeletedAttachments.add(language);

      return {
        ...prev,
        deletedAttachments: Array.from(updatedDeletedAttachments), // Convert Set back to Array
      };
    });
  };

  useEffect(() => {
    dispatch(fetchPromotions({ companyId, page, limit: rowsPerPage }));
    dispatch(fetchLanguages());
  }, [dispatch, companyId, page]);

  useEffect(() => {
    if (promotionsContent?.data) {
      setLocalPromotions(promotionsContent.data);
    }
  }, [promotionsContent]);

  const handleAttachmentsChange = (newAttachments) => {
    setPromotionData((prevNews) => {
      const prevAttachments = prevNews.attachments || {};

      return {
        ...prevNews,
        attachments: {
          ...prevAttachments,
          [`attachment_${currentLanguage}`]:
            newAttachments.report ??
            prevAttachments[`attachment_${currentLanguage}`],

          mainImage: newAttachments.hasOwnProperty("mainImage")
            ? newAttachments.mainImage
            : (prevAttachments.mainImage ?? null),
        },
      };
    });
  };

  const initializePromotionData = () => {
    const initialData = {};
    languagesList.forEach((language) => {
      initialData[language.code] = {
        title: "",
        brief: "",
        link_label: "",
        link_path: "",
        yt_url: "",
        delay: null,
      };
    });
    return initialData;
  };

  const handleDeleteClick = (id, title) => {
    setPromotionIdToDelete(id);
    setDeleteDialogOpen(true);
    setDeleteTitle(title);
  };

  const confirmDelete = () => {
    dispatch(deleteSectionData({ id: promotionIdToDelete }))
      .then(() => {
        setDeleteDialogOpen(false);
        setPromotionIdToDelete(null);

        // Get updated total records after deletion
        const updatedTotalRecords =
          (promotionsContent?.pagination?.totalRecords ??
            promotionsContent?.length ??
            0) - 1;

        const updatedTotalPages = Math.ceil(updatedTotalRecords / rowsPerPage);
        const newPage = page > updatedTotalPages ? updatedTotalPages : page;

        setPage(newPage); // Sync local page state if you're using it

        dispatch(
          fetchPromotions({ companyId, page: newPage, limit: rowsPerPage })
        );
      })
      .catch((error) => {
        console.error("Error deleting promotion:", error);
      });
  };

  const handleAddPromotion = () => {
    setRowToEdit(null);
    setMedia(null);
    setPromotionData(initializePromotionData());
    setCurrentLanguage(currentLanguage);
    setIsAddingPromotion(true);
  };

  const handleEditPromotion = async (id) => {
    try {
      setRowToEdit(id);

      const actualId = id && typeof id === "object" ? id.id : id;

      const promotionData = await dispatch(getPromotion({ id: actualId })).then(
        (res) => res.payload
      );

      if (
        promotionData?.status === "error" &&
        promotionData.message === "Record not found"
      ) {
        alert("Record not found.");
        return;
      }
      if (!promotionData || !promotionData.data) return;

      const eventId = promotionData.data?.id;
      const mainImage = promotionData.data?.mainImage || "";

      const availableLanguages = languagesList.map((lang) => lang.code);

      const attachments = availableLanguages
        .filter((lang) => promotionData.data[lang]?.attachment)
        .map((lang) => ({
          languageId: lang,
          fileName: promotionData.data[lang].attachment,
        }));

      const mediaData = buildMedia(companyId, eventId, mainImage, attachments);
      setMedia(mediaData);

      const newPromotionData = availableLanguages.reduce((acc, lang) => {
        acc[lang] = { ...promotionData.data?.[lang] };
        return acc;
      }, {});

      setPromotionData({
        ...newPromotionData,
        attachments: {
          mainImage: promotionData.data?.mainImage || null,
          report: promotionData.data?.attachments?.report || [],
        },
        deleteMainImage: false,
        deletedAttachments: [],
      });

      setCurrentLanguage(defaultLanguage);
      setIsAddingPromotion(true);
    } catch (error) {
      console.error("Error fetching event data:", error);
    }
  };

  const handleInputChange = (language, field, value) => {
    setPromotionData((prev) => ({
      ...prev,
      [language]: {
        ...prev[language],
        [field]: value,
      },
    }));
  };

  const handleSaveLanguage = () => {
    const languageCodes = languagesList.map((lang) => lang.code);
    const currentIndex = languageCodes.indexOf(currentLanguage);

    if (currentIndex === languageCodes.length - 1) {
      // Finalize and close without dialog
      handleFinalizePromotion();
      setIsAddingPromotion(false); // Close the form
    } else {
      setConfirmationDialogOpen(true); // Show dialog for next step
    }
  };

  const handleProceedToNextLanguage = () => {
    const languageCodes = languagesList.map((lang) => lang.code);
    const currentIndex = languageCodes.indexOf(currentLanguage);
    const nextIndex = currentIndex + 1;
    setCurrentLanguage(languageCodes[nextIndex]);
    setConfirmationDialogOpen(false);
  };

  const handleFinalizePromotion = async (closeForm = true) => {
    let hasValidLanguageData = false;
    const payload = rowToEdit
      ? { id: typeof rowToEdit === "object" ? rowToEdit.id : rowToEdit }
      : {};

    const files = {};

    Object.entries(promotionData).forEach(([lang, data]) => {
      if (availableLanguages.includes(lang)) {
        const hasData =
          data.title?.trim() ||
          data.brief?.trim() ||
          data.link_label?.trim() ||
          data.link_path?.trim() ||
          data.yt_url?.trim() ||
          data.delay?.trim();
        if (hasData) {
          hasValidLanguageData = true;
          payload[lang] = {
            title: data.title?.trim() || "",
            brief: data.brief?.trim() || "",
            link_label: data.link_label?.trim() || "",
            link_path: data.link_path?.trim() || "",
            yt_url: data.yt_url?.trim() || "",
            delay: data.delay || null,
          };
        }
      }
    });

    if (!hasValidLanguageData) {
      alert("Please fill at least one language field before finalizing.");
      return;
    }

    // Handle main image
    if (
      promotionData?.attachments?.mainImage &&
      promotionData?.attachments?.mainImage.length
    ) {
      files["mainImage"] = promotionData.attachments.mainImage[0].file;
    }

    // Handle attachments per language
    availableLanguages.forEach((lang) => {
      if (promotionData.attachments?.[`attachment_${lang}`]?.length) {
        files[`attachment_${lang}`] =
          promotionData.attachments[`attachment_${lang}`][0].file;
      }
    });

    // ✅ Only include deleteMainImage if it's true
    if (rowToEdit && promotionData.deleteMainImage) {
      payload.deleteMainImage = true;
    }

    if (rowToEdit && promotionData.deletedAttachments?.length > 0) {
      payload.deletedAttachments = promotionData.deletedAttachments; // Now just an array ["en", "fr"]
    }

    try {
      await dispatch(savePromotion({ payload, sectionId, files })).unwrap();
      await dispatch(
        fetchPromotions({ companyId, page, limit: rowsPerPage })
      ).unwrap(); // Ensure fresh data is fetched
      setPromotionData({});
      if (closeForm) {
        setIsAddingPromotion(false);
      }
      setConfirmationDialogOpen(false);
      setRowToEdit(null);
    } catch (error) {
      console.error("Error saving promotion:", error);
    }
  };
  const handleExitAndSave = () => {
    handleFinalizePromotion();
    setConfirmationDialogOpen(false); // Close the dialog
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    // Reorder the items in local state
    const reorderedItems = Array.from(localPromotions);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    // Update local state for immediate UI feedback
    setLocalPromotions(reorderedItems);

    // Prepare the payload for dispatch with the reordered data
    const reorderedData = reorderedItems?.map((item, index) => ({
      id: item.id,
      sortIndex: index + 1, // Assuming that you want to track the index for sorting
    }));

    // Dispatch the action to persist changes in the order
    dispatch(orderSectionData(reorderedData))
      .unwrap()
      .then(() => {
        // Optionally refetch promotions if needed
        dispatch(fetchPromotions({ companyId, page, limit: rowsPerPage }));
      })
      .catch((error) => {
        console.error("Error updating order:", error);
      });
  };

  const handleShowGallery = (id, title) => {
    setGalleryId(id);
    setGalleryDialogOpen(true);
    setDeleteTitle(title);
  };

  return (
    <StyledContainer>
      <Paper elevation={3} sx={{ padding: 3 }}>
        {!isAddingPromotion && (
          <>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Promotions Section
            </Typography>
            <StyledButton
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddPromotion}
              sx={{ marginBottom: 2 }}
            >
              Add
            </StyledButton>
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
              <TextField
                type="number"
                label="Edit by ID"
                variant="outlined"
                value={rowToEdit}
                onChange={(e) => setRowToEdit(e.target.value)}
                size="small"
                sx={{ marginRight: 1 }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleEditPromotion(parseInt(rowToEdit))}
                disabled={!rowToEdit}
              >
                Edit
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="promotionsTable">
                  {(provided) => (
                    <Table ref={provided.innerRef} {...provided.droppableProps}>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell width="5%"></StyledTableCell>
                          <StyledTableCell width="65%">Title </StyledTableCell>
                          <StyledTableCell width="15%">Actions</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(promotionsContent?.data || []).map((item, index) => (
                          <Draggable
                            key={item.id}
                            draggableId={item.id.toString()}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <TableRow
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  backgroundColor: snapshot.isDragging
                                    ? "#f5f5f5"
                                    : "inherit",
                                }}
                              >
                                <TableCell {...provided.dragHandleProps}>
                                  <DragIndicatorIcon />
                                </TableCell>
                                <TableCell>{item.title || "N/A"}</TableCell>
                                <TableCell>
                                  <IconButton
                                    onClick={() => handleEditPromotion(item)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    onClick={() =>
                                      handleDeleteClick(item.id, item.title)
                                    }
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                  <IconButton
                                    onClick={() =>
                                      handleShowGallery(item.id, item.title)
                                    }
                                  >
                                    <CollectionsIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    </Table>
                  )}
                </Droppable>
              </DragDropContext>
            </TableContainer>
            <Box mt={2} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          </>
        )}

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete{" "}
              <span style={{ fontWeight: "bold" }}> {deleteTitle}</span>? This
              action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button color="error" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <GalleryUpload
          galleryDialogOpen={galleryDialogOpen}
          setGalleryDialogOpen={setGalleryDialogOpen}
          deleteTitle={deleteTitle}
          fk_sectionDataId={galleryId}
          companyId={companyId}
        />

        {isAddingPromotion && (
          <>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <IconButton onClick={() => setIsAddingPromotion(false)}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">Promotion</Typography>
            </Box>

            <Tabs
              value={currentLanguage}
              onChange={(e, newValue) => setCurrentLanguage(newValue)}
            >
              {languagesList.map((language) => (
                <Tab
                  key={language.code}
                  value={language.code}
                  label={language.name}
                />
              ))}
            </Tabs>

            <TextField
              sx={{ mt: 2 }}
              label="Title"
              value={promotionData[currentLanguage]?.title || ""}
              onChange={(e) =>
                handleInputChange(currentLanguage, "title", e.target.value)
              }
              fullWidth
            />

            <TextField
              sx={{ mt: 2 }}
              label="Brief"
              value={promotionData[currentLanguage]?.brief || ""}
              onChange={(e) =>
                handleInputChange(currentLanguage, "brief", e.target.value)
              }
              fullWidth
            />

            <TextField
              sx={{ mt: 2 }}
              label="Link Label"
              value={promotionData[currentLanguage]?.link_label || ""}
              onChange={(e) =>
                handleInputChange(currentLanguage, "link_label", e.target.value)
              }
              fullWidth
            />

            <TextField
              sx={{ mt: 2 }}
              label="Link Path"
              value={promotionData[currentLanguage]?.link_path || ""}
              onChange={(e) =>
                handleInputChange(currentLanguage, "link_path", e.target.value)
              }
              fullWidth
            />

            <TextField
              label="YouTube URL"
              fullWidth
              variant="outlined"
              sx={{ mt: 2 }}
              value={promotionData[currentLanguage]?.yt_url || ""}
              onChange={(e) =>
                handleInputChange(currentLanguage, "yt_url", e.target.value)
              }
            />

            <TextField
              type="number"
              label="Delay"
              fullWidth
              variant="outlined"
              sx={{ mt: 2 }}
              value={promotionData[currentLanguage]?.delay || ""}
              onChange={(e) =>
                handleInputChange(currentLanguage, "delay", e.target.value)
              }
            />

            <Attachments
              onAttachmentsChange={handleAttachmentsChange}
              media={media}
              currentLanguage={currentLanguage}
              onDeleteMainImage={handleDeleteMainImage} // Pass delete flag handler
              onDeleteAttachment={handleDeleteAttachment} // Pass
            />

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                color="error"
                variant="contained"
                onClick={() => setIsAddingPromotion(false)}
              >
                Cancel
              </Button>
              <Button
                sx={{ ml: 2 }}
                color="primary"
                variant="contained"
                onClick={handleSaveLanguage}
              >
                Save / Skip
              </Button>
            </Box>

            <Dialog
              open={confirmationDialogOpen}
              onClose={() => setConfirmationDialogOpen(false)}
            >
              <DialogTitle>Proceed to Next Language?</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Do you want to proceed to the next language or finalize this
                  promotion?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleExitAndSave} color="primary">
                  Exit And Save
                </Button>
                <Button onClick={handleProceedToNextLanguage} color="primary">
                  Next Language
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Paper>
    </StyledContainer>
  );
}
